import { Calendar, MapPin, Ticket } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { Link } from 'react-router-dom';
import { HomePageEvent } from 'store/events/types';
import { EventImageType } from '../../../types/EventState';
import {
  formatDateAndTimeIntervalOnTimeZone,
  getEventDetailsLink,
  getStartingPrice,
  getTicketsPageLink,
} from '../../../utils/events';
import ShareEventButton from '../ShareEventButton';
import PrimaryButton from '../PrimaryButton';
import { Button } from '../ui/button';
import { messages } from './messages';
import { EVENT_TYPE_FREE } from 'utils/static/constants';

interface EventCardProps {
  event: HomePageEvent;
}

const EventCard: React.FC<EventCardProps> = ({
  event: {
    id,
    id_seller,
    name,
    type,
    location,
    cover,
    minmaxticketsprices,
    timeInterval: { startsAt, endsAt },
    dateTimeFormat: { dateFormat, timeFormat, timezone },
    fees_and_taxes: { tax_included, tax_rate },
    currency,
  },
}) => {
  const { t } = useTranslation();

  const eventFormattedDate =
    !startsAt && !endsAt
      ? t(messages.perpetual())
      : formatDateAndTimeIntervalOnTimeZone(
          timezone,
          dateFormat,
          timeFormat,
          startsAt as string,
          endsAt as string,
          t('common.at'),
        );

  const eventLink = getEventDetailsLink(name, id);
  const buyTicketsLink = getTicketsPageLink(name, id, type);

  let displayedImage: EventImageType = {
    original: '',
    small: '',
  };

  if (cover) {
    displayedImage = { ...cover };
  }

  return (
    <div className="w-full min-w-[290px] max-w-[350px] mx-auto shadow-sm rounded-lg overflow-hidden border relative group">
      <div className="absolute top-3 right-3 bg-neutral-0 opacity-80 hover:opacity-100 rounded-lg border shadow-md group-hover:block md:hidden z-10">
        <ShareEventButton vendorId={id_seller} eventId={id} eventName={name} />
      </div>
      <Link to={eventLink} className="block w-full h-[148px] p-2 ">
        {displayedImage.original ? (
          <LazyLoadImage
            alt={name}
            height={132}
            src={displayedImage.original}
            effect="blur"
            width={'100%'}
            wrapperClassName="[&>img]:h-full [&>img]:object-cover rounded-lg overflow-hidden"
          />
        ) : (
          <div className="h-full bg-gradient-to-r from-primary from-40% to-neutral-800 rounded-lg" />
        )}
      </Link>
      <div>
        <div className="p-4 pt-2">
          <Link to={eventLink}>
            <h3 className="title-sm cursor-pointer mt-[4px] sm:h-[48px] line-clamp-2">
              {name}
            </h3>
          </Link>
          <div className="flex gap-2 mt-[12px] sm:h-[32px]">
            <Calendar size={16} className="min-w-[20px] mt-[1px]" />
            <p className="p3-medium text-neutral-800">{eventFormattedDate}</p>
          </div>
          <div className="flex gap-2 mt-2 sm:mt-1">
            <MapPin size={16} className="min-w-[20px] mt-[1px]" />
            <p className="p3-medium line-clamp-1 text-neutral-800">
              {location}
            </p>
          </div>
        </div>

        <div className="w-full flex justify-between items-center px-[16px] py-[12px] text-neutral-1000 font-semibold">
          {minmaxticketsprices ? (
            <>
              <div className="flex items-center">
                <Ticket size={20} className="min-w-[20px]" />
                <span className="p1-medium ml-[8px]">
                  {type === EVENT_TYPE_FREE
                    ? t('common.free')
                    : t('vendorEventsPage.from') +
                      ' ' +
                      getStartingPrice(
                        minmaxticketsprices,
                        currency,
                        tax_included,
                        tax_rate,
                      )}
                </span>
              </div>
              <Button asChild>
                <Link to={buyTicketsLink}>{t(messages.tickets_btn())}</Link>
              </Button>
            </>
          ) : (
            <PrimaryButton
              className="p1-medium ml-auto text-[16px]"
              label={t('common.saleEnded')}
              disabled
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCard;
